import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';

// Derive ws(s):// URL from the backend URL (never hardcode).
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
export const WS_URL = BACKEND_URL.replace(/^http/, 'ws') + '/api/ws';

const LiveContext = createContext(null);

/**
 * LiveDataProvider: a single shared WebSocket connection to the platform
 * realtime hub. Components subscribe to topics via useLiveChannel and receive
 * message envelopes. Handles reconnect with backoff and exposes a connection
 * status used to render proper connection-state UI (never fake data).
 */
export const LiveDataProvider = ({ children }) => {
  const wsRef = useRef(null);
  const subs = useRef(new Map()); // topic -> Set(callback)
  const topicsRef = useRef(new Set());
  const retry = useRef(0);
  const mounted = useRef(true);
  const [status, setStatus] = useState('connecting'); // connecting | online | offline

  const scheduleReconnect = useCallback((connectFn) => {
    if (!mounted.current) return;
    retry.current = Math.min(retry.current + 1, 6);
    setTimeout(() => { if (mounted.current) connectFn(); }, 700 * retry.current);
  }, []);

  const connect = useCallback(() => {
    let ws;
    const token = localStorage.getItem('itw_admin_token') || localStorage.getItem('itw_token');
    const url = WS_URL + (token ? `?token=${encodeURIComponent(token)}` : '');
    try {
      ws = new WebSocket(url);
    } catch (e) {
      setStatus('offline');
      scheduleReconnect(connect);
      return;
    }
    wsRef.current = ws;
    ws.onopen = () => {
      setStatus('online');
      retry.current = 0;
      topicsRef.current.forEach((t) => {
        try { ws.send(JSON.stringify({ action: 'subscribe', topic: t })); } catch (e) { /* noop */ }
      });
    };
    ws.onmessage = (e) => {
      let msg;
      try { msg = JSON.parse(e.data); } catch (err) { return; }
      const topic = msg.domain || msg.topic;
      if (topic && subs.current.has(topic)) {
        subs.current.get(topic).forEach((cb) => { try { cb(msg); } catch (err) { /* noop */ } });
      }
    };
    ws.onclose = () => { setStatus('offline'); scheduleReconnect(connect); };
    ws.onerror = () => { try { ws.close(); } catch (e) { /* noop */ } };
  }, [scheduleReconnect]);

  useEffect(() => {
    mounted.current = true;
    connect();
    const reconnectOnAuth = () => { try { wsRef.current && wsRef.current.close(); } catch (e) { /* noop */ } };
    window.addEventListener('itw:auth-changed', reconnectOnAuth);
    return () => {
      mounted.current = false;
      window.removeEventListener('itw:auth-changed', reconnectOnAuth);
      try { wsRef.current && wsRef.current.close(); } catch (e) { /* noop */ }
    };
  }, [connect]);

  const subscribe = useCallback((topic, cb) => {
    if (!subs.current.has(topic)) subs.current.set(topic, new Set());
    subs.current.get(topic).add(cb);
    topicsRef.current.add(topic);
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      try { ws.send(JSON.stringify({ action: 'subscribe', topic })); } catch (e) { /* noop */ }
    }
    return () => {
      const set = subs.current.get(topic);
      if (!set) return;
      set.delete(cb);
      if (set.size === 0) {
        subs.current.delete(topic);
        topicsRef.current.delete(topic);
        const w = wsRef.current;
        if (w && w.readyState === WebSocket.OPEN) {
          try { w.send(JSON.stringify({ action: 'unsubscribe', topic })); } catch (e) { /* noop */ }
        }
      }
    };
  }, []);

  return <LiveContext.Provider value={{ status, subscribe }}>{children}</LiveContext.Provider>;
};

export const useLive = () => useContext(LiveContext);

export function useLiveChannel(topic, onTick) {
  const ctx = useContext(LiveContext);
  useEffect(() => {
    if (!ctx || !topic || !onTick) return undefined;
    return ctx.subscribe(topic, onTick);
  }, [ctx, topic, onTick]);
  return ctx ? ctx.status : 'offline';
}
